import { Injectable } from '@angular/core';
import { DragDropRegistry, DragRef } from '@angular/cdk/drag-drop';
import { effectiveZoom } from './effective-zoom';

/**
 * CDK drag under the density zoom (GEN-E-100-05).
 *
 * `DragRef` moves its root element with a `transform: translate3d()` built from raw `clientX/Y`
 * deltas (screen px). Under a CSS `zoom` the browser scales that transform again, so a free-dragged
 * element (the floating XatBot launcher, the magic bar, the presenter strip) lands at cursor x zoom —
 * at compact a 150px drag moved it 127.5px — and the siblings a sortable list shifts aside are off by
 * the same factor.
 *
 * CDK already divides both by `DragRef.scale` (the public `cdkDragScale` input). This makes `scale`
 * read the root element's `effectiveZoom` at the moment CDK uses it, multiplied by whatever a template
 * binds. Dialogs dragged by their `.cdk-overlay-pane` sit at a net zoom of 1 (the pane container is
 * counter-zoomed), so they are untouched; comfortable is 1 everywhere.
 */
export function makeDragRefZoomAware(ref: DragRef): void {
  let boundScale = ref.scale;
  Object.defineProperty(ref, 'scale', {
    configurable: true,
    enumerable: true,
    get: () => boundScale * effectiveZoom(ref.getRootElement()),
    // CdkDrag re-syncs its `cdkDragScale` input onto the ref before each drag.
    set: (value: number) => {
      boundScale = value > 0 && Number.isFinite(value) ? value : 1;
    }
  });
}

/**
 * Every `DragRef` registers itself here from its constructor, and this registry is root-provided — unlike
 * `DragDrop`, which `DragDropModule` re-provides in every lazy module that imports it. So one provider in
 * `AppModule` reaches every `cdkDrag` in the app, lazy or standalone.
 */
@Injectable()
export class ZoomAwareDragDropRegistry extends DragDropRegistry {
  override registerDragItem(drag: DragRef): void {
    super.registerDragItem(drag);
    makeDragRefZoomAware(drag);
  }
}
